<template>
    <v-card class="elevation-12">
        <v-toolbar :color="getConfig('login.color')" dark dense>
            <v-toolbar-title>{{ getConfig('login.title') }}</v-toolbar-title>
        </v-toolbar>

        <v-card-text class="pt-4 pb-0">

            <p v-if="isSessionExpired">
                Votre session à expirée, vous pouvez choisir de rester sur cette page ou vous pouvez vous reconnecter.
            </p>

            <ValidationObserver ref="observer">
                <ValidationProvider v-slot="{ errors }" name="Client" rules="required">
                    <v-autocomplete v-show="$store.state.application.localEnv" v-model="providerCode" :items="providers"
                        placeholder="Client" prepend-icon="fas fa-users" menu-props="offsetY" :error-messages="errors"
                        :backgroundColor="getConfig('login.text_input_bg_color')" outlined dense />
                </ValidationProvider>

                <v-row class="ml-4 mt-2">
                    <v-col class="ml-2 pt-0">
                        Votre identifiant correspond à l’adresse e-mail utilisée lors de votre réservation.
                    </v-col>
                </v-row>
                <ValidationProvider v-slot="{ errors }" name="Email" rules="required|email">
                    <v-text-field v-model="email" placeholder="Email" prepend-icon="fas fa-envelope"
                        class="autofill-background" @keydown.enter="submit" :error-messages="errors"
                        :backgroundColor="getConfig('login.text_input_bg_color')" outlined dense />
                </ValidationProvider>

                <ValidationProvider v-slot="{ errors }" name="Mot de passe" rules="required">
                    <v-text-field v-model="password" placeholder="Mot de passe" @keydown.enter="submit"
                        prepend-icon="fas fa-key" :type="showPassword ? 'text' : 'password'"
                        @click:append="showPassword = !showPassword" class="autofill-background"
                        :append-icon="showPassword ? 'fas fa-eye' : 'fas fa-eye-slash'" :error-messages="errors"
                        :backgroundColor="getConfig('login.text_input_bg_color')" outlined dense />
                </ValidationProvider>
            </ValidationObserver>

            <v-row class="ml-4">
                <v-col class="ml-1">
                    <v-btn @click="submit()" class="mr-2"
                        :color="['lamotte', 'europeanhomes'].includes($store.state.application.providerCode) ? getConfig('login.color') : '#0fc6a4'"
                        dark small> Je me connecte </v-btn>

                    <ForgottenPasswordModal :initEmail="email">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" :color="getConfig('login.forgotten_password_bg_color')"
                                :style="{ color: getConfig('login.forgotten_password_fg_color') }" dark small> Mot de
                                passe oublié ? </v-btn>
                        </template>
                    </ForgottenPasswordModal>
                </v-col>
            </v-row>

            <v-row class="ml-4">
                <v-col class="ml-1 pt-0">
                    <small class="text-center align-self-center" v-html="getConfig('login.sub_message')"></small>
                </v-col>
            </v-row>
        </v-card-text>

        <v-card-actions class="px-4">
            <v-spacer />

            <v-btn v-if="isSessionExpired" @click="$emit('close')" small> Rester </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import ForgottenPasswordModal from './ForgottenPasswordModal.vue';

export default {
    name: 'BuyerLoginCard',

    props: {
        isSessionExpired: { type: Boolean, default: false }
    },

    components: {
        ForgottenPasswordModal
    },

    data: () => ({
        email: '',
        password: '',

        showPassword: false,

        providers: []
    }),

    methods: {
        async submit() {
            try {
                const valid = await this.$refs.observer.validate();
                if (!valid) {
                    return;
                }

                this.setLoading(true);
                const body = {
                    type: 'buyer',
                    login: this.email,
                    password: this.password,
                    providerCode: this.providerCode
                };
                const { success, err } = await this.repos.auth.login(body);
                if (success) {
                    this.$notify({
                        title: 'Bienvenue',
                        text: 'Bienvenue sur votre espace client',
                        type: 'success'
                    });

                    if (this.isSessionExpired) {
                        this.$emit('close');
                    } else {
                        if (this.$route.query.redirect) {
                            this.$router.push({ path: this.$route.query.redirect, hash: this.$route.query.hash });
                        } else if (this.$route.query.client_id && this.$route.query.redirect_uri) {
                            this.submitsso();
                        } else {
                            this.$router.push('/');
                        }
                    }
                } else {
                    throw new Error(err);
                }
            } catch (err) {
                console.error(err);
                this.$notify({
                    title: 'Erreur',
                    text: err.message,
                    type: 'error'
                });
            } finally {
                this.setLoading(false);
            }
        },

        async submitsso() {
            try {
                const valid = await this.$refs.observer.validate();
                if (!valid) {
                    return;
                }
                const { client_id, redirect_uri, response_type, kc_idp_hint, scope, state, nonce } = this.$route.query;

                this.setLoading(true);
                const body = {
                    type: 'buyersso',
                    login: this.email,
                    password: this.password,
                    providerCode: this.providerCode,
                    client_id,
                    redirect_uri,
                    response_type,
                    kc_idp_hint,
                    state, scope, nonce
                };
                const { success, err, auth_code, url } = await this.repos.auth.ssoLogin(body);


                if (success) {
                    this.$notify({
                        title: 'Bienvenue',
                        text: 'Bienvenue sur votre espace client',
                        type: 'success'
                    });

                    if (this.isSessionExpired) {
                        this.$emit('close');
                    } else {
                        if (success && auth_code && url) {
                            window.location.href = url;
                            window.location.replace(url);
                        }
                    }


                } else {
                    throw new Error(err);
                }
            } catch (err) {
                console.error(err);
                this.$notify({
                    title: 'Erreur',
                    text: err.message,
                    type: 'error'
                });
            } finally {
                this.setLoading(false);
            }
        },

        async fetchProviders() {
            try {
                const {
                    providers
                } = await this.repos.providers.getAvailables();
                this.providers = providers;
            } catch (err) {
                console.error(err);
            }
        }
    },

    computed: {
        providerCode: {
            get() {
                return this.$store.state.application.providerCode;
            },

            set(providerCode) {
                this.$store.commit('application/setProviderCode', providerCode);
            }
        }
    },

    created() {
        this.fetchProviders();
        document.documentElement.style.setProperty('--autofill-background-color', this.getConfig('login.text_input_bg_color'));
    }
};
</script>

<style lang="scss" scoped>
.autofill-background::v-deep input:autofill {
    box-shadow: 0 0 0px 1000px var(--autofill-background-color) inset;
    -webkit-box-shadow: 0 0 0px 1000px var(--autofill-background-color) inset;
}
</style>